/**
 * WCL CRM case hierarchy for the website "Get in Touch" enquiry form.
 *
 * The CRM organizes cases as category → type → subtype. For website enquiries
 * the category and type are fixed; only the subtype varies with the topic the
 * user picks. These IDs come from the WCL Postman collection ("Contact Us"
 * body) and were confirmed live against GetCaseTypes / GetCaseSubType:
 *
 *   casecategory = WEBSITE_CASE_CATEGORY_ID  (GetCaseTypes `Id`)
 *   caseType     = WEBSITE_CASE_TYPE_ID      (GetCaseSubType `Id`)
 *   subtype      = the selected topic's `wcl_casesubtypeid`
 */
export const WEBSITE_CASE_CATEGORY_ID = '14f739c8-217d-ec11-8d21-6045bda5d69b';
// Production caseType (GetCaseSubType `Id`), from the go-live "Contact Us" collection.
export const WEBSITE_CASE_TYPE_ID = 'eff8c06f-cb81-f111-ab0f-6045bdcf1b7e';

/** A CRM case subtype = one topic in the "What would you like to connect about?" dropdown. */
export interface WCLCaseSubtype {
  /** `wcl_casesubtypeid` — sent to CreateCaseWebsite as `subtype`. */
  id: string;
  /** `wcl_name` — the human-readable topic label. */
  name: string;
}

/**
 * Match a CMS lead-form name to the CRM subtype that must be sent to
 * CreateCaseWebsite. CMS and CRM labels are not always identical, so keep the
 * same small set of known semantic fallbacks for every form that reuses the
 * lead-form topic list.
 */
export function matchCrmSubtype(
  formName: string,
  topics: WCLCaseSubtype[] | null | undefined,
): WCLCaseSubtype | undefined {
  if (!topics?.length) return undefined;
  const norm = (value: string) => value.toLowerCase().replace(/[^a-z]/g, '');
  const target = norm(formName);
  const exact = topics.find((topic) => norm(topic.name) === target);
  if (exact) return exact;

  const name = formName.toLowerCase();
  const byKeyword = (keys: string[]) =>
    topics.find((topic) => keys.some((key) => topic.name.toLowerCase().includes(key)));
  if (name.includes('dealer')) return byKeyword(['dealer']);
  if (name.includes('retail')) return byKeyword(['retail']);
  if (name.includes('cement')) return byKeyword(['cement']);
  if (name.includes('home')) return byKeyword(['home']);
  if (name.includes('media') || name.includes('corporate')) {
    return byKeyword(['media', 'corporate']);
  }
  if (name.includes('partner') || name.includes('associ')) {
    return byKeyword(['partner', 'associ']);
  }
  if (name.includes('support') || name.includes('feedback')) {
    return byKeyword(['support', 'feedback']);
  }
  if (name.includes('career')) return byKeyword(['career']);
  return undefined;
}

/** Raw upstream shape (GetCaseSubType → ReturnValue.CaseSubTypeMaster[]). */
interface RawCaseSubtype {
  wcl_casesubtypeid?: string;
  wcl_name?: string;
}

export interface GetCaseSubTypeResponse {
  Success?: boolean;
  ReturnValue?: {
    TotalRows?: string;
    CaseSubTypeMaster?: RawCaseSubtype[];
  } | null;
}

/** Normalize the upstream envelope into a flat, client-safe topic list. */
export function normalizeCaseSubtypes(
  res: GetCaseSubTypeResponse | null | undefined,
): WCLCaseSubtype[] {
  const rows = res?.ReturnValue?.CaseSubTypeMaster ?? [];
  return rows
    .filter((r): r is Required<RawCaseSubtype> => Boolean(r?.wcl_casesubtypeid && r?.wcl_name))
    .map((r) => ({ id: r.wcl_casesubtypeid, name: r.wcl_name }));
}
